import { datadogRum } from '@datadog/browser-rum';
import type { CardfinderSessionType } from '@/db/tables';

const SESSION = 'session';

/** Mirror of the funnel answer state; owned by `useLocalAnswerHydration`. */
export const ANSWERS_KEY = 'answers';

const STORAGE_PREFIX = 'cardfinder:funnel:';

export function readSession<T>(key: string): T | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  } catch (error) {
    datadogRum.addError(error instanceof Error ? error : new Error('Session storage read failed'));
    return undefined;
  }
}

export function writeSession<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch (error) {
    datadogRum.addError(error instanceof Error ? error : new Error('Session storage write failed'));
  }
}

export function removeFromSession(key: string): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(STORAGE_PREFIX + key);
}

/**
 * The backend session this tab's answers belong to.
 *
 * `recovered` carries no id: those answers came from `GET ?status=complete`,
 * which strips all metadata, so there is no write target to record.
 *
 * `attemptedAs` is the identity class the completion write was *issued* under,
 * derived from cookie presence — so it is an intent, not a record of what the
 * server stored. It lets an anonymous run be re-saved once under a real
 * identity without that path looping.
 */
export type LocalFunnelSession =
  | { kind: 'in-progress'; id: string }
  | { kind: 'complete'; id: string; attemptedAs: CardfinderSessionType }
  | { kind: 'recovered' };

export function readLocalSession(): LocalFunnelSession | undefined {
  return readSession<LocalFunnelSession>(SESSION);
}

/** True once the run is finished, so the next edit must start a new one. */
export function shouldRotateLocalSession(session: LocalFunnelSession | undefined): boolean {
  return session?.kind === 'complete' || session?.kind === 'recovered';
}

export function markLocalSessionInProgress(id: string): void {
  writeSession<LocalFunnelSession>(SESSION, { kind: 'in-progress', id });
}

export function markLocalSessionComplete(id: string, attemptedAs: CardfinderSessionType): void {
  writeSession<LocalFunnelSession>(SESSION, { kind: 'complete', id, attemptedAs });
}

/** Records that the on-screen answers came from the caller's saved session. */
export function adoptRecoveredSession(): void {
  writeSession<LocalFunnelSession>(SESSION, { kind: 'recovered' });
}

/**
 * Rotate: drop the backend session but keep the answers, so editing a finished
 * run starts a new session with the funnel still pre-populated.
 */
export function resetLocalSession() {
  removeFromSession(SESSION);
}

/** Discard: abandon the run entirely. Only for paths that also reset answers. */
export function discardFunnelRun() {
  resetLocalSession();
  removeFromSession(ANSWERS_KEY);
}
